import type { AdministratieRow } from '@/api/administratie';
import { displayProjectName } from '@/utils/format';

/** One werf (project) of the day, with its task × installer rows. */
export interface WerfGroup {
  key: string;
  projectName: string;
  klant: string | null;
  werfAdres: string | null;
  checkinWorkNr: string | null;
  projectleider: string | null;
  projectId: number | null;
  /** At least one task of the werf has a sale order to hold the Checkin@work number. */
  hasSaleOrder: boolean;
  rows: AdministratieRow[];
}

/**
 * Group the day's rows per werf (project), groups ordered by project name, rows
 * inside a group in their incoming order. A task without project is its own
 * group. The header fields come from the first row that has them: tasks of one
 * project share the sale order, so they normally agree.
 */
export function groupByWerf(rows: AdministratieRow[]): WerfGroup[] {
  const groups = new Map<string, WerfGroup>();
  for (const row of rows) {
    const key = row.projectId != null ? `p-${row.projectId}` : `t-${row.taskId}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        projectName: displayProjectName(row.projectName),
        klant: null,
        werfAdres: null,
        checkinWorkNr: null,
        projectleider: null,
        projectId: row.projectId,
        hasSaleOrder: false,
        rows: [],
      };
      groups.set(key, group);
    }
    group.klant ??= row.klant;
    group.werfAdres ??= row.werfAdres;
    group.checkinWorkNr ??= row.checkinWorkNr;
    group.projectleider ??= row.projectleider;
    group.hasSaleOrder ||= row.hasSaleOrder;
    group.rows.push(row);
  }
  return [...groups.values()].sort((a, b) => a.projectName.localeCompare(b.projectName, 'nl'));
}

/**
 * Is anyone on this werf to collect documents for? A one-person installer is
 * always the person; an installer with workers counts once a worker is marked
 * present. The API decides the same way and refuses an empty zip.
 */
export function werfHasPeople(group: WerfGroup): boolean {
  return group.rows.some((r) => r.installerId != null && (r.workers == null || r.presenceReported));
}
